import { createFileRoute } from "@tanstack/react-router";
import { Mail, Instagram, Twitter, MessageCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchSettings } from "@/lib/api";

export const Route = createFileRoute("/tentang")({
  loader: async ({ context: { queryClient } }) => {
    return await queryClient.ensureQueryData({
      queryKey: ["settings"],
      queryFn: fetchSettings,
    });
  },
  head: ({ loaderData }) => {
    const settings = loaderData as any;
    const siteName = settings?.site_name || "Lihar Aksara";
    const canonicalUrl = "https://liharaksara.web.id/tentang";
    const title = `Tentang — ${siteName}`;
    const description = `Tentang ${siteName}, ruang sunyi untuk membaca dan merenung. Pelajari filosofi dan tim di balik layar.`;

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  component: Page,
});

function Page() {
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  }) as any;

  const siteName = settings?.site_name || "Lihar Aksara";
  const siteEmail = settings?.site_email || "halo@aksara.id";
  const twitter = settings?.social_twitter;
  const instagram = settings?.social_instagram;
  const whatsapp = settings?.whatsapp_number;

  return (
    <div className="container-prose py-20 md:py-32">
      <div className="text-xs uppercase tracking-[0.2em] text-accent mb-4">Tentang</div>
      <h1 className="font-serif text-5xl md:text-7xl font-medium leading-[1.02]">
        {siteName} adalah ruang untuk membaca.
      </h1>

      <div className="mt-14 article-body">
        <p>
          {siteName} dimulai dari sebuah kegelisahan sederhana: bahwa ruang untuk membaca tulisan
          panjang di internet semakin sempit, dikejar oleh kebisingan, iklan yang menyilaukan, dan
          algoritma yang tergesa-gesa.
        </p>

        <h2>Filosofi</h2>
        <p>
          Kami percaya bahwa membaca adalah bentuk paling jujur dari mendengarkan. Karena itu kami
          membangun ruang ini sepelan mungkin — sedikit warna, banyak ruang putih, tipografi yang
          dibuat untuk dinikmati, bukan untuk dilewati.
        </p>

        <blockquote>
          Tidak setiap tulisan harus segera dibalas. Tidak setiap halaman harus segera diakhiri.
        </blockquote>

        <h2>Tim</h2>
        <p>
          {siteName} dikelola oleh tim kecil yang terdiri dari editor, penulis, dan satu desainer
          yang menolak menggunakan terlalu banyak warna. Kami menerbitkan tulisan secara teratur,
          tidak banyak, tetapi dipilih with hati-hati.
        </p>

        <h2>Hubungi Kami</h2>
        <p>Untuk pengajuan naskah, kerjasama, atau sekadar menyapa — silakan tulis kepada kami.</p>
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-3">
        <a
          href={`mailto:${siteEmail}`}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-foreground text-background text-sm hover:bg-accent hover:text-accent-foreground transition-colors font-medium"
        >
          <Mail size={16} className=" " /> {siteEmail}
        </a>
        {whatsapp && (
          <a
            href={`https://wa.me/${whatsapp}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-accent font-medium"
          >
            <MessageCircle size={16} className=" " /> WhatsApp
          </a>
        )}
        {instagram && (
          <a
            href={`https://instagram.com/${instagram}`}
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-accent"
            aria-label="Instagram"
          >
            <Instagram size={16} className=" " />
          </a>
        )}
        {twitter && (
          <a
            href={`https://twitter.com/${twitter}`}
            target="_blank"
            rel="noreferrer"
            className="p-3 rounded-md border border-border hover:bg-muted transition-colors text-muted-foreground hover:text-accent"
            aria-label="Twitter"
          >
            <Twitter size={16} className=" " />
          </a>
        )}
      </div>
    </div>
  );
}
